import { Component, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExtranetAuthenticationService } from '@core/services/extranet-authentication.service';
import { environment } from '@env';
import { StorageKey } from '@src/app/core/enums/storage-key.enum';
import { NewsInfo } from '@src/app/core/models/news-info.model';
import { News } from '@src/app/core/models/news.model';
import { EnvironmentService } from '@src/app/core/services/environment.service';
import { ExtranetMenuService } from '@src/app/core/services/extranet-menu.service';
import { HeaderService } from '@src/app/core/services/header.service';
import { NewsService } from '@src/app/core/services/news.service';
import { SelectItem } from 'primeng/api';
import { EMPTY, Subject, interval, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import {
  AuthenticationService,
  BaseComponent,
  Configuration,
  RouteStateService,
  StorageService,
  UserProfileService,
} from 'sigv-library';
import { UserProfile } from 'sigv-library/model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @Input() isAtLogin?: boolean;
  isVisible = true;
  locale = '';
  currentLanguageLabel = '';
  languageOptions: SelectItem[] = [];
  newsInfo!: News;
  currentUser?: UserProfile;
  userName = '';
  isAzureADEnable: boolean;
  isExtranet: boolean;
  newsRefreshFrequency: number;

  filterList: any[] | undefined;

  private readonly destroy$ = new Subject();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private routeStateService: RouteStateService,
    injector: Injector,
    private extranetMenuService: ExtranetMenuService,
    private userProfileService: UserProfileService,
    private authenticationService: AuthenticationService,
    private extranetAuthenticationService: ExtranetAuthenticationService,
    private configuration: Configuration,
    private newsService: NewsService,
    private storageService: StorageService,
    private headerService: HeaderService
  ) {
    super(injector);
    const urlLocale = this.route.snapshot.queryParams.lang;
    const localeVal = urlLocale ?? this.storageService.lang.getItem();
    this.languageOptions = this.appConfig.languageList.map((x) => ({
      label: x.name,
      value: x.value,
    }));
    this.locale =
      this.languageOptions.find((x) => x.value === localeVal)?.value ?? 'en';
    this.currentLanguageLabel =
      this.languageOptions.find((item) => item.value === this.locale)?.label ||
      'English';
    this.isAzureADEnable = this.appConfig.getAzureAD().enabled;
    this.isExtranet = environment.isExtranet;
    this.storageService.lang.setItem(this.locale);
    this.translateService.use(this.locale);
    this.newsRefreshFrequency =
      EnvironmentService.environmentConfig.newsRefreshFrequency;
  }

  ngOnInit(): void {
    this.headerService
      .getHeaderObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isOpen) => {
          this.isVisible = isOpen;
        },
      });
    this.userProfileService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentUser) => {
        if (currentUser) {
          this.currentUser = currentUser;
          this.userName = currentUser.userName.split(/(?=[A-Z])/)[0];
        }
      });
    if (!this.isAtLogin) {
      this.autoRefreshQueryNews();
      this.newsService.updateNotification
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.queryNews();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSideMenu(): void {
    this.extranetMenuService.toggleSideMenu();
  }

  changeLanguage(event: any, languageOption: SelectItem): void {
    event.preventDefault();
    this.locale = languageOption.value;
    this.currentLanguageLabel = languageOption.label || '';
    this.translateService.use(this.locale);
    this.storageService.lang.setItem(this.locale);
  }

  logout(): void {
    if (environment.isExtranet) {
        this.router.navigate(['/logout']);
    } else {
      this.authenticationService.logout();
      this.router.navigate([this.configuration.routePath.login]);
    }
  }

  updateToDo(): void {
    this.extranetMenuService.updateTodoListCount();
  }

  updateNotification(): void {
    this.newsService.updateNotification.next();
  }

  goHome(): void {
    this.router.navigate([this.configuration.routePath.index], {
      state: { isApply: true },
    });
  }

  goChangePassword(): void {
    this.router.navigate(['/changePassword']);
  }

  private queryNews(): void {
    this.newsService
      .queryNews()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.rtnCode === 0 && res.data) {
          this.newsInfo = res.data;
        }
      });
  }

  private autoRefreshQueryNews(): void {
    const refreshFrequency = this.newsRefreshFrequency || 5; // default 5 mins
    const period = refreshFrequency * 60 * 1000;

    interval(period)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.queryNews());
  }

  navigateTodo(info: NewsInfo): void {
    switch (info.caseStatus) {
      case '50':
      case '190':
      case '210':
        this.newsService
          .notification([info.id])
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.router.navigate(['/search/detail'], {
              queryParams: { caseNo: info.caseNo },
            });
          });
        break;
      // 保險專區 - 報價完成
      case '-3':
      // 保險專區 - 投保完成
      case '-4':
        this.newsService
          .notification([info.id])
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.storageService.session.setItem(
              StorageKey.INSURANCE_LIST_DEFAULT_QUERY_PARAMS,
              {
                caseNo: info.caseNo,
              }
            );
            this.routeStateService.navigateTo('/insurance/list');
          });
        break;
      default:
        this.newsService
          .notification([info.id])
          .pipe(
            takeUntil(this.destroy$),
            switchMap(() =>
              this.newsService.checkTodoExist({
                caseNo: info.caseNo,
                todoListId: info.todoListId ? info.todoListId : '',
              })
            ),
            switchMap((res) => {
              if (res.rtnCode === 0) {
                if (!res.data) {
                  this.toastService.info(
                    this.translateService.instant('News.NotExist')
                  );
                  return EMPTY;
                } else {
                  return of(null);
                }
              } else {
                return EMPTY;
              }
            })
          )
          .subscribe(() => {
            switch (info.caseStatus) {
              case '30':
              case '20':
              case '40':
                this.router.navigate(['/returned'], {
                  queryParams: { actionType: 'return', caseNo: info.caseNo },
                });
                break;
              case '170':
                this.router.navigate(
                  ['/todo/pending-commencement-doc-detail'],
                  {
                    queryParams: { caseNo: info.caseNo },
                  }
                );
                break;
              case '70':
                this.router.navigate(['/todo/download-lou'], {
                  queryParams: {
                    action: 'detail',
                    louFileId: info.louFileId,
                    needAgree: info.needAgree,
                    caseNo: info.caseNo,
                  },
                });
                break;
              // 下載合約
              case '-1':
                this.router.navigate(['/todo/download-agreement-detail'], {
                  queryParams: {
                    caseNo: info.caseNo,
                    todoStatus: info.downloadAgreementStatus,
                  },
                });
                break;
              // 下載設定文件
              case '-2':
                this.router.navigate(['/todo/ehakmilik-download-detail'], {
                  queryParams: { todoId: info.todoListId },
                });
                break;
              // 預覽合約
              case '-5':  
                this.router.navigate(['/todo/agreement-prep-todo-detail'], {
                  queryParams: { userRole: this.currentUser?.extranetUserRole ,caseNo: info.caseNo },
                });
                break;
            }
          });
    }
  }

  navigateDetail(): void {
    this.router.navigate(['/news-detail']);
  }
}
